import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Users, Award, TrendingUp, AlertTriangle } from 'lucide-react';
import {
  BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

const COLORS = ['#10B981', '#F59E0B', '#EF4444'];

export default function MapaTurma({ year, trimestre, turmas, initialTurmaId }) {
  const [loading, setLoading] = useState(false);
  const [selectedTurma, setSelectedTurma] = useState(initialTurmaId || '');
  const [data, setData] = useState(null);
  
  useEffect(() => {
    if (initialTurmaId && !selectedTurma) setSelectedTurma(initialTurmaId);
  }, [initialTurmaId]);
  
  useEffect(() => {
    if (selectedTurma) {
      loadData();
    }
  }, [selectedTurma, year, trimestre]);
  
  const loadData = async () => {
    setLoading(true);
    try {
      const params = { year };
      if (trimestre) params.trimestre = trimestre;
      
      const res = await api.get(`/central-notas/mapa-turma/${selectedTurma}`, { params });
      setData(res.data);
    } catch (error) {
      console.error('Erro ao carregar mapa:', error);
      toast.error('Erro ao carregar dados da turma');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div className="space-y-6" data-testid="mapa-turma">
      {/* Seletor de Turma */}
      <Card className="bg-white border border-purple-200/50 shadow-sm">
        <CardContent className="py-4">
          <div className="flex items-center gap-4">
            <div className="p-2 rounded-lg bg-blue-100">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-slate-500">Selecione uma turma para visualizar</p>
            </div>
            <Select value={selectedTurma} onValueChange={setSelectedTurma}>
              <SelectTrigger className="w-[300px] bg-slate-50 border-slate-200">
                <SelectValue placeholder="Selecione a turma..." />
              </SelectTrigger>
              <SelectContent className="bg-white max-h-[300px]">
                {turmas.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
      
      {!selectedTurma ? (
        <Card className="bg-white border border-purple-200/50 shadow-sm">
          <CardContent className="py-16 text-center">
            <Users className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">Selecione uma Turma</h3>
            <p className="text-slate-500">Escolha uma turma acima para ver o mapa detalhado</p>
          </CardContent>
        </Card>
      ) : loading ? (
        <div className="space-y-4">
          <Skeleton className="h-32 bg-slate-100" />
          <div className="grid grid-cols-2 gap-4">
            <Skeleton className="h-64 bg-slate-100" />
            <Skeleton className="h-64 bg-slate-100" />
          </div>
        </div>
      ) : data && (
        <>
          {/* KPIs da Turma */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
              <CardContent className="py-4">
                <p className="text-blue-100 text-sm">Média Geral</p>
                <p className="text-3xl font-bold">{data.turma?.media_geral?.toFixed(1)}</p>
              </CardContent>
            </Card>
            <Card className="bg-white border border-slate-200">
              <CardContent className="py-4">
                <p className="text-slate-500 text-sm">Total de Alunos</p>
                <p className="text-3xl font-bold text-slate-800">{data.turma?.total_alunos}</p>
              </CardContent>
            </Card>
            <Card className="bg-white border border-slate-200">
              <CardContent className="py-4">
                <p className="text-slate-500 text-sm">Recuperações</p>
                <p className="text-3xl font-bold text-orange-600">{data.turma?.total_recuperacoes}</p>
              </CardContent>
            </Card>
            <Card className="bg-white border border-slate-200">
              <CardContent className="py-4">
                <p className="text-slate-500 text-sm">Turma</p>
                <p className="text-lg font-bold text-slate-800 truncate">{data.turma?.nome}</p>
              </CardContent>
            </Card>
          </div>
          
          {/* Gráficos */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Média por Disciplina */}
            <Card className="bg-white border border-purple-200/50 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-slate-800 text-base">Média por Disciplina</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.medias_disciplina} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis type="number" domain={[0, 10]} stroke="#94A3B8" />
                      <YAxis type="category" dataKey="disciplina" stroke="#94A3B8" width={100} tick={{ fontSize: 11 }} />
                      <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #E2E8F0', borderRadius: '8px' }} />
                      <Bar dataKey="media" radius={[0, 4, 4, 0]}>
                        {data.medias_disciplina?.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            
            {/* Distribuição por Status */}
            <Card className="bg-white border border-purple-200/50 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-slate-800 text-base">Distribuição por Status</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={data.distribuicao_status}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={80}
                        paddingAngle={3}
                        dataKey="valor"
                      >
                        {data.distribuicao_status?.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
          
          {/* Ranking de Alunos */}
          <Card className="bg-white border border-purple-200/50 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-slate-800 text-base flex items-center gap-2">
                <Award className="w-5 h-5 text-yellow-500" />
                Ranking da Turma
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {data.ranking_alunos?.slice(0, 15).map((aluno) => (
                  <div 
                    key={aluno.student_id}
                    className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200"
                  >
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold ${
                      aluno.posicao === 1 ? 'bg-yellow-500' :
                      aluno.posicao === 2 ? 'bg-gray-400' :
                      aluno.posicao === 3 ? 'bg-orange-500' : 'bg-slate-400'
                    }`}>
                      {aluno.posicao}
                    </span>
                    {aluno.foto ? (
                      <img src={aluno.foto || "/default-avatar.png"} onError={(e) => { e.currentTarget.src = "/default-avatar.png"; }} alt={aluno.nome} className="w-8 h-8 rounded-full object-cover" />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 text-xs font-bold">
                        {aluno.nome?.charAt(0)}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{aluno.nome}</p>
                    </div>
                    <span className={`font-bold ${
                      aluno.media >= 7 ? 'text-green-600' :
                      aluno.media >= 5 ? 'text-orange-600' : 'text-red-600'
                    }`}>
                      {aluno.media?.toFixed(1)}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
